import { select } from "@fruugo/utilities";
import { addItemToCartRequest } from "../api";
import { openModal } from "../components/modal/Modal";
import { LoadingModalContent } from "../components/modal/LoadingModalContent";
import { ProductAddedToCartModalContent } from "../components/modal/ProductAddedToCartModalContent";
import { sendAddToCartEvent } from "../analytics/google-analytics";
import { sendFacebookPixelEvent } from "../analytics/facebook-pixel";
import { i18n } from "../i18n";
import { ModalType } from "../components/modal/types";
import { sendRTBAddToCartEvent } from "../analytics/rtb-house";
import { sendPinterestAddToCartEvent } from "../analytics/pinterest";
import { sendTikTokAddToCartEvent } from "../analytics/tiktok";

declare let window: ProductPage;

const { cheapestShippingMethod } = window;

export function handleAddToCart(skuId: string, quantity: number) {
  const [_, updateModal] = openModal(
    ModalType.ProductAddedToCart,
    <LoadingModalContent />,
    {
      ariaLabel: i18n("common.loading"),
    }
  );

  addItemToCartRequest(skuId, quantity)
    .then((response) => {
      const {
        addedCartItem,
        cartValid,
        success,
        checkoutUrl,
        message,
        quantity,
      } = response;
      const { skuInfo, productId, retailerInformation } = window;

      const { title, titleBeforeTranslation, description } = skuInfo;
      const {
        numericTotalInclTax,
        currencyCode,
        numericTotalInclTaxAfterDiscount,
      } = skuInfo.price;

      updateModal(
        <ProductAddedToCartModalContent
          cartValid={cartValid}
          success={success}
          checkoutUrl={checkoutUrl}
          message={message}
          addedCartItem={addedCartItem}
          quantity={quantity}
        />,
        {
          ariaLabel: i18n("product.modal.addedtocart"),
          ariaDescribedby: "added-to-cart-aria-description",
        }
      );

      if (response.success === true) {
        window.totalCartItems = response.totalItemsInCart;

        const cartItems = select<HTMLElement>(".js-cart-counter");
        if (cartItems) {
          cartItems.classList.remove("d-none");
          cartItems.innerText = response.totalItemsInCart.toString();
        }
      }

      sendAddToCartEvent(
        skuInfo,
        quantity,
        {
          buy_now: false,
        },
        {
          sku_id: skuInfo.id,
          retailer_id: retailerInformation.id,
          retailer_name: retailerInformation.name,
          retailer_country: retailerInformation.country,
          days_to_dispatch: skuInfo.shipping.daysToDispatch,
          days_to_deliver: cheapestShippingMethod?.daysToDelivery,
          shipping_cost: cheapestShippingMethod?.numericPrice,
          free_shipping: cheapestShippingMethod?.free,
          item_available: skuInfo.sellable,
          availability_status: skuInfo.availabilityStatus,
          shipping_from_country: skuInfo?.shipping.originCountryCode,
          item_grade: skuInfo.analyticsData?.grade,
          item_condition: skuInfo.analyticsData?.condition ?? "New",
        }
      );
      sendFacebookPixelEvent("AddToCart", {
        value:
          skuInfo.price.numericTotalInclTaxAfterDiscount &&
          skuInfo.price.numericTotalInclTax,
        currency: skuInfo.price.currencyCode,
        contents: [{ id: skuInfo.productId, quantity }],
        content_type: "product",
      });

      sendRTBAddToCartEvent(productId);
      sendPinterestAddToCartEvent(
        productId,
        title ?? titleBeforeTranslation,
        numericTotalInclTaxAfterDiscount ?? numericTotalInclTax,
        quantity,
        currencyCode
      );
      sendTikTokAddToCartEvent(
        productId,
        title ?? titleBeforeTranslation,
        currencyCode,
        description,
        quantity,
        numericTotalInclTaxAfterDiscount ?? numericTotalInclTax
      );
    })
    .catch(() => {
      updateModal(
        <ProductAddedToCartModalContent
          cartValid={false}
          success={false}
          message={i18n("cart.generic.error")}
        />,
        {
          ariaLabel: i18n("product.modal.addedtocart"),
          ariaDescribedby: "added-to-cart-aria-description",
        }
      );
    });
}
